import type { CardCollection } from './build';
import type { CharacterSave } from './character';
import type { EquipItem } from './items';

/** Roster-wide (account) state shared by all characters. */
export interface AccountSave {
  version: number;
  characters: CharacterSave[];
  lastCharacterId: string | null;
  wallet: { silver: number; gold: number };
  /** Legacy (roster) experience: characters' XP feeds it. */
  legacyXp: number;
  cards: CardCollection;
  collectibles: {
    seeds: string[];
    hearts: string[];
    vistas: string[];
    lore: string[];
    fieldBosses: string[];
  };
  lifeEnergy: number;
  lifeEnergyAt: number;
  haven: HavenState;
  achievements: Record<string, number>;
  settings: Settings;
  /** Session-day clock (see dayCycle). */
  day: number;
  week: number;
  market: { listings: MarketListing[]; lastRefresh: number };
  mail: MailItem[];
  /** Guild membership (the key opens the guild's private room). */
  guild: { id: string; key: string; name: string; tag: string; rank: 'leader' | 'member' } | null;
  ship: { level: number; hull: number; crew: string[] };
  playTime: number;
}

export interface HavenState {
  level: number;
  xp: number;
  research: Record<string, number>;
  /** Research in progress: id → completion timestamp (ms). */
  researching: { id: string; doneAt: number } | null;
  crafting: { recipe: string; count: number; doneAt: number }[];
  dispatches: { mission: string; crew: string[]; doneAt: number }[];
  crew: string[];
  garden: { plantedAt: number; crop: string } | null;
}

export interface Settings {
  master: number;
  music: number;
  sfx: number;
  /** Graphics preset, or 'auto' to pick one from the device and adapt to the frame rate. */
  quality: 'auto' | 'low' | 'medium' | 'high';
  shake: number;
  swapMouse: boolean;
  showDamage: boolean;
  hudScale: number;
  bindings: Record<string, string>;
  playerName: string;
  flashReduction: boolean;
  sessionDays: boolean;
  /** Play online (peer-to-peer) with other players. */
  online: boolean;
}

export interface MarketListing {
  id: string;
  item: string;
  count: number;
  price: number;
  seller: string;
  player: boolean;
}

export interface MailItem {
  id: string;
  from: string;
  subject: string;
  body: string;
  items: { id: string; count: number }[];
  /** Gear, accessories or stones that did not fit in the bag. */
  equip?: EquipItem[];
  silver: number;
  gold: number;
  read: boolean;
  at: number;
}

export const ACCOUNT_VERSION = 2;
export const LIFE_ENERGY_MAX = 1000;
/** Energy regenerated per real minute. */
export const LIFE_ENERGY_REGEN = 3;
export const MAX_LEGACY_RANK = 30;

export const defaultSettings = (): Settings => ({
  master: 0.8,
  music: 0.55,
  sfx: 0.8,
  quality: 'auto',
  shake: 1,
  swapMouse: false,
  showDamage: true,
  hudScale: 1,
  bindings: {},
  playerName: '',
  flashReduction: false,
  sessionDays: true,
  online: true,
});

export function newAccount(): AccountSave {
  return {
    version: ACCOUNT_VERSION,
    characters: [],
    lastCharacterId: null,
    wallet: { silver: 500, gold: 0 },
    legacyXp: 0,
    cards: { owned: {}, dupes: {}, deck: [null, null, null, null, null, null] },
    collectibles: { seeds: [], hearts: [], vistas: [], lore: [], fieldBosses: [] },
    lifeEnergy: LIFE_ENERGY_MAX,
    lifeEnergyAt: Date.now(),
    haven: { level: 1, xp: 0, research: {}, researching: null, crafting: [], dispatches: [], crew: ['Tam', 'Oriel', 'Brisk'], garden: null },
    achievements: {},
    settings: defaultSettings(),
    day: 1,
    week: 1,
    market: { listings: [], lastRefresh: 0 },
    mail: [],
    guild: null,
    ship: { level: 1, hull: 100, crew: [] },
    playTime: 0,
  };
}

/** Legacy rank thresholds: rank r needs 4000 * r^1.4 cumulative XP. */
export const legacyXpFor = (rank: number): number => Math.round(4000 * Math.pow(rank, 1.4));
export function legacyRank(xp: number): number {
  let r = 1;
  while (r < MAX_LEGACY_RANK && xp >= legacyXpFor(r)) r++;
  return r;
}

/** Brings life energy up to date with real time. */
export function tickLifeEnergy(a: AccountSave, now = Date.now()): void {
  const minutes = Math.max(0, (now - a.lifeEnergyAt) / 60000);
  const gain = Math.floor(minutes * LIFE_ENERGY_REGEN);
  if (gain > 0) {
    a.lifeEnergy = Math.min(LIFE_ENERGY_MAX, a.lifeEnergy + gain);
    a.lifeEnergyAt += (gain / LIFE_ENERGY_REGEN) * 60000;
  }
  if (a.lifeEnergy >= LIFE_ENERGY_MAX) a.lifeEnergyAt = now;
}

/** Collectible bonuses: hearts +1% HP each, every 10 seeds +0.5% attack. */
export function collectibleBonuses(a: AccountSave): { hp: number; atk: number; skillPoints: number } {
  const hearts = a.collectibles.hearts.length;
  const seeds = a.collectibles.seeds.length;
  return { hp: hearts * 0.01, atk: Math.floor(seeds / 10) * 0.005, skillPoints: Math.floor(seeds / 10) * 2 + hearts * 2 };
}
